'use client';

import { useState, useCallback, useContext } from 'react';
import { Dialog, Flex, Text, Input, Button, Pill } from '@/_components';
import type { TaxReturnIdentityVerificationDialogProps } from './TaxReturnIdentityVerificationDialog.types';
import { AdminTaxReturnsContext } from '../../AdminTaxReturnsProvider';
import { deformatPhoneNumber } from '@/_utils/phoneNumbers';
import { updateTaxReturnIdentityVerification } from '@/_api/admin-tax-returns';
import { UserContext } from '@/_contexts/UserProvider';
import { ToastContext } from '@/_contexts/ToastProvider';

export const TaxReturnIdentityVerificationDialog = ({
  isOpen,
}: TaxReturnIdentityVerificationDialogProps) => {
  const {
    setSelectedIdentityVerificationReturn,
    selectedIdentityVerificationReturn,
  } = useContext(AdminTaxReturnsContext);
  const { token } = useContext(UserContext);
  const { addToast } = useContext(ToastContext);

  const [phoneNumbers, setPhoneNumbers] = useState<Record<number, string>>({});
  const [updatedVerifications, setUpdatedVerifications] = useState<
    Record<number, string>
  >({});

  const verifications =
    selectedIdentityVerificationReturn?.tax_return_identity_verifications;

  const onClose = () => {
    setSelectedIdentityVerificationReturn(undefined);
    setPhoneNumbers({});
    setUpdatedVerifications({});
  };

  const onUpdatePhoneNumber = useCallback(
    async (id: number, phoneNumber: string) => {
      try {
        const deFormattedPhoneNumber = deformatPhoneNumber(phoneNumber);
        const { error } = await updateTaxReturnIdentityVerification({
          id: selectedIdentityVerificationReturn?.id!,
          companyId: selectedIdentityVerificationReturn?.company_id!,
          token,
          verificationId: id,
          phoneNumber: deFormattedPhoneNumber,
        });

        if (error) {
          addToast('error', 'Failed to update phone number');
        } else {
          setUpdatedVerifications((prev) => ({
            ...prev,
            [id]: deFormattedPhoneNumber,
          }));
          setPhoneNumbers((prev) => ({
            ...prev,
            [id]: deFormattedPhoneNumber,
          }));
          addToast('success', 'Phone number updated successfully');
        }
      } catch (error) {
        addToast('error', 'Failed to update phone number', { error });
      }
    },
    [selectedIdentityVerificationReturn, token, addToast],
  );

  const handlePhoneNumberChange = (id: number, value: string) => {
    setPhoneNumbers((prev) => ({ ...prev, [id]: value }));
  };

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title="Identity Verification">
      <Dialog.Content>
        <Flex isColumn gap={8}>
          {verifications?.map((verification) => {
            const currentPhoneNumber =
              phoneNumbers[verification.id] ?? verification.phone_number;
            const updatedPhoneNumber =
              updatedVerifications[verification.id] ??
              verification.phone_number;
            const hasChanged = currentPhoneNumber !== updatedPhoneNumber;

            return (
              <Flex key={verification.id} isYCentered isBetween={true}>
                <Flex gap={6} isYCentered isBetween={true}>
                  <Text fontWeight="bold">{verification.name}</Text>
                  <Pill label={verification.title} intent="secondary" />
                </Flex>
                <Flex gap={8} isYCentered>
                  <Input
                    label="Phone Number"
                    isLabelHidden={true}
                    value={currentPhoneNumber}
                    mask="(___) ___-____"
                    onChange={(e) =>
                      handlePhoneNumberChange(verification.id, e.target.value)
                    }
                    placeholder={'(---) --- ----'}
                  />
                  <Button
                    intent="primary"
                    onClick={() =>
                      onUpdatePhoneNumber(verification.id, currentPhoneNumber)
                    }
                    isDisabled={!hasChanged}
                    value="Update"
                  />
                </Flex>
              </Flex>
            );
          })}
        </Flex>
      </Dialog.Content>
    </Dialog>
  );
};
